import React, { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import AppRoutes from './routes';
import { useAuthPersistence } from './hooks/useAuthPersistence';
import { setupAuthDiagnostics, logAuthenticationState } from './utils/authDiagnostics';
import axios from 'axios';
import { setupInterceptors } from './services/apiInterceptors';
import PrivacyPolicyChecker from './components/Profile/PrivacyPolicyComponent/PrivacyPolicyChecker';
import MobileGate from './mobile/MobileGate';
import RegistrationWelcome from './components/Auth/RegistrationWelcome';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <>
      {!isHomePage && <div style={{ height: 50, flexShrink: 0 }} aria-hidden />}
      {children}
    </>
  );
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AuthManager: React.FC = () => {
  useAuthPersistence();

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // 🚨 OTIMIZAÇÃO: Evitar inicializações duplicadas (React StrictMode)
    const isAlreadyInitialized = (window as Window & { __padelmaster_initialized?: boolean }).__padelmaster_initialized;
    if (isAlreadyInitialized) {
      return;
    }
    (window as Window & { __padelmaster_initialized?: boolean }).__padelmaster_initialized = true;

    // Simplified initialization - only in dev mode
    if (import.meta.env.DEV) {
      console.log('🚀 PadelMaster v3.2.0 inicializando...');
    }


    // 🔐 Intercetores no axios global (o authService e o apiUtils usam-no
    // diretamente): Authorization automático + 401 → refresh → retry.
    // A instância `api` de services/api.ts liga os seus na própria criação.
    setupInterceptors(axios);

    // 🔧 Configurar diagnósticos de autenticação
    setupAuthDiagnostics();

    // 📊 Log do estado inicial (com debounce)
    logAuthenticationState();

    // ⚡ Sistema de versioning otimizado já carrega automaticamente no Footer

    if (import.meta.env.DEV) {
      console.log('✅ PadelMaster inicializado');
    }
  }, []);


  return (
    <Provider store={store}>
      <BrowserRouter>
        <ScrollToTop />
        <AuthManager />
        <PrivacyPolicyChecker />
        <MobileGate
          desktop={
            <Layout>
              <AppRoutes />
            </Layout>
          }
        />
        <RegistrationWelcome />
      </BrowserRouter>
    </Provider>
  );
};

export default App;
